import { useState } from 'react';
import {
  Stack, Group, Button, Textarea, Divider,
} from '@mantine/core';
import { Select as CCMSSelect } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useFacilitatorService,
  FacilitatorModelView,
  FacilitatorStatus,
  UpdateFacilitatorRequest,
} from '@actschurch/ccms-sdk';

interface EditTabProps {
  facilitator: FacilitatorModelView;
  /** Status lookup, fetched once by the parent page. */
  statuses: FacilitatorStatus[];
  onSaved: () => void;
}

export default function EditTab({ facilitator, statuses, onSaved }: EditTabProps) {
  const Facilitators = useFacilitatorService();

  // `isActive` is intentionally not editable here — the FacilitatorStatus lookup below is the
  // real status. The current value is carried through the payload so saving never flips it.
  const [form, setForm] = useState<UpdateFacilitatorRequest>({
    bio: facilitator.bio ?? '',
    isActive: facilitator.isActive,
  });
  const [statusID, setStatusID] = useState<number>(facilitator.statusID);

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Facilitators.update(facilitator.userId, form);

      // Status lives on its own endpoint, so it's a second call — only when it actually changed.
      if (statusID !== facilitator.statusID) {
        await Facilitators.changeStatus(facilitator.userId, statusID);
      }

      onSaved();
      toast.success('Updated', 'Facilitator updated successfully.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to update facilitator.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="md">
      <CCMSSelect
        label="Status"
        placeholder="Select a status"
        data={statuses.map((s) => ({
          value: String(s.statusID),
          label: s.name ?? `Status ${s.statusID}`,
        }))}
        value={String(statusID)}
        onChange={(val) => setStatusID(Number(val) || facilitator.statusID)}
      />

      <Textarea
        label="Biography"
        placeholder="Facilitator biography"
        value={form.bio ?? ''}
        onChange={(e) => setForm({ ...form, bio: e.currentTarget.value })}
        minRows={3}
      />

      <Divider my="sm" />

      <Group justify="flex-end">
        <Button loading={saving} onClick={handleSave}>
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
}
